import { shapes } from '@joint/plus';
import AddIcon from '@mui/icons-material/Add';
import CodeIcon from '@mui/icons-material/Code';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box, Button, Chip, IconButton, MenuItem, Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import PlaceholderPicker from '../PropertyPanel/PlaceholderPicker';
import { insertAtCursor } from '../PropertyPanel/insertAtCursor';
import UpstreamFieldsHint from './UpstreamFieldsHint';
import { useBaseInspector } from './useBaseInspector';
import VariableAutocompletePicker, { useVariablesCatalog } from './VariableAutocompletePicker';

/**
 * SetVariableInspector — Item #1 roadmap chatbot JOINT (06/10/2026 → refactor 07/10/2026).
 *
 * Caixa SÍNCRONA que executa N operações sobre VARIÁVEIS (`tb_integration_user_variable`,
 * placeholder `@variavel@`). Síncrona = sem pausa, segue direto pela porta "out" depois
 * de aplicar.
 *
 * Decisões de produto (07/10/2026 — refactor Opção A):
 *   - Autocomplete SELECT-ONLY (sem freeSolo). Admin DEVE escolher variável existente.
 *   - Botão "+ Nova variável" abre modal inline — cria no cadastro oficial via POST
 *     /integration-user-variables, mesmo endpoint usado pela tela de CRUD de variáveis.
 *     Vantagem: variável nova vira cidadã de 1ª classe no Mexx (aparece em outras caixas,
 *     é auditável, deletável, tem tipo correto).
 *   - Depois de criar, a nova var é auto-selecionada na operação ativa — zero fricção.
 *
 * Rejeitado: freeSolo com auto-create em runtime. Fere o modelo (variável nasceria
 * silenciosa na 1ª passada do chat, sem auditoria clara, sem aparecer no CRUD).
 *
 * Backend: `ChatbotFlowController` case `app.SetVariable` → `ISetVariableBoxService.ApplyAsync`
 * → `GetIdByName` strict (sem auto-create). Se variável referenciada no fluxo foi deletada
 * entre save e runtime, backend loga warning e PULA (não quebra fluxo).
 */

interface Props {
    cell: shapes.app.Base;
    chatbotFlowId: string;
}

type Operation = {
    id: string;              // interno (UI only)
    variable: string;        // name da variável (vai pro backend)
    operator: string;
    value: string;
};

const cellProps = {
    label: ['attrs', 'label', 'text'],
    operationsJson: ['attrs', 'operations', 'text'],
};

// Operadores expostos na UI — mesmos valores que o backend SetVariableOperators conhece.
const OPERATORS: Array<{ value: string; labelKey: string; defaultLabel: string; hint: string }> = [
    { value: '=',     labelKey: 'setvariable.op.assign', defaultLabel: 'atribuir (=)',        hint: 'variável := valor' },
    { value: '+=',    labelKey: 'setvariable.op.add',    defaultLabel: 'somar / concat (+=)', hint: 'número soma; texto concatena' },
    { value: '-=',    labelKey: 'setvariable.op.sub',    defaultLabel: 'subtrair (-=)',       hint: 'apenas numérico' },
    { value: '*=',    labelKey: 'setvariable.op.mul',    defaultLabel: 'multiplicar (*=)',    hint: 'apenas numérico' },
    { value: '/=',    labelKey: 'setvariable.op.div',    defaultLabel: 'dividir (/=)',        hint: 'apenas numérico (÷0=0)' },
    { value: 'clear', labelKey: 'setvariable.op.clear',  defaultLabel: 'limpar (clear)',      hint: 'zera o valor' },
];

const UNARY_OPS = new Set(['clear']);

const genId = (prefix: string) => `${prefix}_${Math.random().toString(36).slice(2, 9)}`;

const SetVariableInspector = (props: Props): ReactElement => {
    const { cell } = props;
    const intl = useIntl();

    const [label, setLabel] = useState<string>('');
    const [operations, setOperations] = useState<Operation[]>([]);
    // Catálogo de variáveis COMPARTILHADO entre as N operações (1 fetch só, não N).
    // Migrado em 13/10/2026 pra VariableAutocompletePicker + useVariablesCatalog —
    // antes esse inspector tinha ~45 linhas inline (state + loadVariables + useEffect)
    // que foram movidas pro hook canônico. Comportamento IDÊNTICO ao anterior.
    const { variables, loading: variablesLoading, reload: reloadVariables } = useVariablesCatalog();

    // Bloqueia o listener 'change' do cell durante nossos próprios writes (padrão ConditionInspector).
    const isSyncingRef = useRef(false);
    const initializedRef = useRef(false);

    // Picker de placeholder — só no campo value.
    const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
    const activeInputRef = useRef<HTMLInputElement | null>(null);

    const assignFormFields = useCallback((): void => {
        if (isSyncingRef.current) return;

        setLabel(cell.prop(cellProps.label) ?? '');
        const raw = cell.prop(cellProps.operationsJson);
        if (!raw) return;
        try {
            const parsed: any[] = JSON.parse(raw);
            if (!Array.isArray(parsed) || parsed.length === 0) return;
            setOperations(parsed.map((op: any) => ({
                id: genId('op'),
                variable: String(op.variable ?? ''),
                operator: String(op.operator ?? '='),
                value: String(op.value ?? ''),
            })));
        } catch {
            // JSON inválido — mantém state atual.
        }
    }, [cell]);

    const changeCellProp = useBaseInspector({ cell, assignFormFields });

    const persist = useCallback((next: Operation[]) => {
        isSyncingRef.current = true;
        try {
            const serialized = next.map((op) => ({
                variable: op.variable,
                operator: op.operator,
                value: UNARY_OPS.has(op.operator) ? '' : op.value,
            }));
            changeCellProp(cellProps.operationsJson, JSON.stringify(serialized));
        } finally {
            queueMicrotask(() => { isSyncingRef.current = false; });
        }
    }, [changeCellProp]);

    useEffect(() => {
        if (initializedRef.current) return;
        initializedRef.current = true;
        const raw = cell.prop(cellProps.operationsJson);
        if (raw) return;
        setOperations([{ id: genId('op'), variable: '', operator: '=', value: '' }]);
    }, [cell]);

    const addOperation = () => {
        const next = [...operations, { id: genId('op'), variable: '', operator: '=', value: '' }];
        setOperations(next);
        persist(next);
    };

    const removeOperation = (id: string) => {
        const next = operations.filter((op) => op.id !== id);
        setOperations(next);
        persist(next);
    };

    const updateOperation = (id: string, patch: Partial<Operation>) => {
        const next = operations.map((op) => op.id === id ? { ...op, ...patch } : op);
        setOperations(next);
        persist(next);
    };

    const moveOperation = (fromIndex: number, direction: 'up' | 'down') => {
        const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
        if (toIndex < 0 || toIndex >= operations.length) return;
        const next = [...operations];
        [next[fromIndex], next[toIndex]] = [next[toIndex], next[fromIndex]];
        setOperations(next);
        persist(next);
    };

    // ========== Modal "+ Nova variável" ==========

    // ========== Render ==========
    // Modal "+ Nova variável" + guard de duplicado + POST /integration-user-variables agora
    // vivem dentro do VariableAutocompletePicker (migração 13/10/2026). Comportamento idêntico.

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
                        {intl.formatMessage({ id: 'setvariable.title', defaultMessage: 'Setar variáveis' })}
                    </Typography>
                    <Tooltip title={intl.formatMessage({
                        id: 'setvariable.title.hint',
                        defaultMessage: 'Executa N operações em sequência sobre variáveis do fluxo. Síncrona — segue direto pra próxima caixa pela saída.',
                    })}>
                        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Tooltip>
                </Stack>

                <TextField
                    label={intl.formatMessage({ id: 'setvariable.label', defaultMessage: 'Rótulo (opcional)' })}
                    size="small"
                    value={label}
                    onChange={(e) => {
                        setLabel(e.target.value);
                        changeCellProp(cellProps.label, e.target.value);
                    }}
                    placeholder={intl.formatMessage({ id: 'setvariable.label.placeholder', defaultMessage: 'Set Variable' })}
                />

                <Stack spacing={1.5}>
                    {operations.map((op, index) => {
                        const isUnary = UNARY_OPS.has(op.operator);
                        // selectedVar + isMissing agora vivem dentro do VariableAutocompletePicker
                        // (migração 13/10/2026). Mesma lógica, só mudou de local.

                        return (
                            <Box
                                key={op.id}
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    p: 1.5,
                                    background: '#fafafa',
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                                    <Chip
                                        label={`${intl.formatMessage({ id: 'setvariable.op.label', defaultMessage: 'Operação' })} ${String(index + 1).padStart(2, '0')}`}
                                        size="small"
                                        sx={{ background: '#8BC34A', color: '#fff', fontWeight: 600 }}
                                    />
                                    <Box sx={{ flex: 1 }} />
                                    {operations.length > 1 && (
                                        <>
                                            <IconButton
                                                size="small"
                                                disabled={index === 0}
                                                onClick={() => moveOperation(index, 'up')}
                                                title={intl.formatMessage({ id: 'setvariable.move.up', defaultMessage: 'Mover pra cima' })}
                                            >
                                                <DragIndicatorIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                disabled={index === operations.length - 1}
                                                onClick={() => moveOperation(index, 'down')}
                                                title={intl.formatMessage({ id: 'setvariable.move.down', defaultMessage: 'Mover pra baixo' })}
                                            >
                                                <DragIndicatorIcon fontSize="small" />
                                            </IconButton>
                                        </>
                                    )}
                                    <IconButton
                                        size="small"
                                        onClick={() => removeOperation(op.id)}
                                        title={intl.formatMessage({ id: 'setvariable.remove', defaultMessage: 'Remover operação' })}
                                        disabled={operations.length === 1}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Stack>

                                <Stack spacing={1}>
                                    {/* Migrado 13/10/2026 pro VariableAutocompletePicker.
                                        Modo CONTROLLED — N operações compartilham 1 catálogo via
                                        useVariablesCatalog() no pai (evita N requests paralelos). */}
                                    <VariableAutocompletePicker
                                        value={op.variable}
                                        onChange={(name) => updateOperation(op.id, { variable: name })}
                                        label={intl.formatMessage({ id: 'setvariable.variable', defaultMessage: 'Variável' })}
                                        placeholder={intl.formatMessage({
                                            id: 'setvariable.variable.placeholder',
                                            defaultMessage: 'Escolha uma variável cadastrada',
                                        })}
                                        missingText={(name) => intl.formatMessage({
                                            id: 'setvariable.variable.missing',
                                            defaultMessage: 'Variável "{name}" não existe mais no cadastro. Escolha outra ou crie uma nova.',
                                        }, { name })}
                                        variables={variables}
                                        loading={variablesLoading}
                                        onReload={reloadVariables}
                                    />

                                    <Select
                                        value={op.operator}
                                        size="small"
                                        onChange={(e) => updateOperation(op.id, { operator: String(e.target.value) })}
                                    >
                                        {OPERATORS.map((o) => (
                                            <MenuItem key={o.value} value={o.value}>
                                                {intl.formatMessage({ id: o.labelKey, defaultMessage: o.defaultLabel })}
                                                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                                    — {o.hint}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    {!isUnary && (
                                        <>
                                            <ValueInput
                                                value={op.value}
                                                onChange={(v) => updateOperation(op.id, { value: v })}
                                                onOpenPicker={(anchor, input) => {
                                                    activeInputRef.current = input;
                                                    setPickerAnchor(anchor);
                                                }}
                                                placeholder={intl.formatMessage({
                                                    id: 'setvariable.value.placeholder',
                                                    defaultMessage: 'Valor literal ou {{placeholder}}',
                                                })}
                                                label={intl.formatMessage({ id: 'setvariable.value', defaultMessage: 'Valor' })}
                                            />
                                            {/* 13/10/2026 — Hint de dot-notation quando `op.value` referencia variável upstream. */}
                                            <UpstreamFieldsHint
                                                cell={cell}
                                                textValue={op.value}
                                                compact
                                                onInsert={(token) => updateOperation(op.id, { value: (op.value ?? '') + token })}
                                            />
                                        </>
                                    )}
                                </Stack>
                            </Box>
                        );
                    })}
                </Stack>

                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addOperation}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    {intl.formatMessage({ id: 'setvariable.add', defaultMessage: 'Adicionar operação' })}
                </Button>
            </Box>

            {/* Modal "+ Nova variável" migrou pro VariableAutocompletePicker (13/10/2026).
                As keys i18n `setvariable.create.*` continuam existindo mas não são mais usadas
                aqui — o picker usa as keys `varpicker.create.*` próprias dele. */}

            <PlaceholderPicker
                open={!!pickerAnchor}
                anchorEl={pickerAnchor}
                onClose={() => setPickerAnchor(null)}
                onInsert={(placeholder) => {
                    insertAtCursor(activeInputRef.current, placeholder);
                    if (activeInputRef.current) {
                        const ev = new Event('input', { bubbles: true });
                        activeInputRef.current.dispatchEvent(ev);
                    }
                    setPickerAnchor(null);
                }}
            />
        </>
    );
};

const ValueInput: React.FC<{
    label: string;
    placeholder: string;
    value: string;
    onChange: (v: string) => void;
    onOpenPicker: (anchor: HTMLElement, input: HTMLInputElement | null) => void;
}> = ({ label, placeholder, value, onChange, onOpenPicker }) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    return (
        <TextField
            label={label}
            size="small"
            fullWidth
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            inputRef={(el) => { inputRef.current = el; }}
            InputProps={{
                endAdornment: (
                    <IconButton
                        size="small"
                        onClick={(e) => onOpenPicker(e.currentTarget as HTMLElement, inputRef.current)}
                        title="Inserir placeholder"
                        sx={{ p: 0.25 }}
                    >
                        <CodeIcon fontSize="small" />
                    </IconButton>
                ),
            }}
        />
    );
};

export default SetVariableInspector;
